import { ConnectedRouter } from 'connected-react-router';
import React from 'react';
import { ReactReduxContext } from 'react-redux';
import { SWRConfig } from 'swr';
import { InjectIntlContext } from '@core/internationalize/context';
import history from '@core/redux/history';
import { ClientProvider } from '@members/api/ClientContext';
import OauthProvider from '@members/auth/OauthProvider';
import UserFetcher from '@members/auth/UserFetcher';
import { BootupEvents } from '@members/bootup-events/bootupEvents';
import { useCopyClient } from '@members/copy/CopyProvider';
import CopyProvider from '@members/copy/CopyProvider/CopyProvider';
import Overlay from '@members/feature-toggles-ui';
import { FidesPopup } from '@members/fides/FidesPopup';
import { GraphqlProvider } from '@members/graphql/GraphqlProvider';
import { Internationalize } from '@members/localization/Internationalize';
import { LocaleSetter } from '@members/localization/LocaleSetter';
import { getAjsUserId } from '@members/optimizely/utils';
import { PersistGate } from '@members/persist/PersistGate';
import { TierProvider } from '@members/tiering/TierProvider';
import { ToggleGate } from '@members/toggle-gate/ToggleGate';
import { env } from '../_lib/env';

// These providers need to be defined outside of the tree where Redux is setup for hooks to work as expected
function AsyncCopyProviders({ children }) {
  const copyClient = useCopyClient();
  return <CopyProvider copyClient={copyClient}>{children}</CopyProvider>;
}

/**
 * Consolidates all global React context providers to a single component
 */
export default function GlobalContextProviders({ children, reduxStore, reduxClient }) {
  return (
    <OauthProvider apiEnv={env.extLink.api} reduxClient={reduxClient}>
      <SWRConfig
        value={{
          revalidateOnFocus: false,
        }}
      >
        <ClientProvider client={reduxClient}>
          <Internationalize>
            <InjectIntlContext>
              <ToggleGate userId={getAjsUserId()}>
                <ConnectedRouter history={history} context={ReactReduxContext}>
                  <UserFetcher>
                    <TierProvider>
                      <LocaleSetter>
                        <PersistGate store={reduxStore}>
                          <GraphqlProvider>
                            <AsyncCopyProviders children={children} />
                            <BootupEvents />
                            <FidesPopup />
                            <Overlay />
                          </GraphqlProvider>
                        </PersistGate>
                      </LocaleSetter>
                    </TierProvider>
                  </UserFetcher>
                </ConnectedRouter>
              </ToggleGate>
            </InjectIntlContext>
          </Internationalize>
        </ClientProvider>
      </SWRConfig>
    </OauthProvider>
  );
}
